import type { DataAction } from '../types.js';
import type { IModalService } from './ModalService.js';
import type { IToastService } from './ToastService.js';
import type { IClipboardService } from './ClipboardService.js';
import type { IUiToggleService } from './UiToggleService.js';
import type { IThemeService } from './ThemeService.js';
import type { IUserActionsService } from './UserActionsService.js';
import type { IDomainActionsService } from './DomainActionsService.js';
import type { IModalConfirmHelper } from './ModalConfirmHelper.js';
import type { ITokenActionsService } from './TokenActionsService.js';
import type { ISettingsActionsService } from './SettingsActionsService.js';
import type { IAuthService } from './AuthService.js';

/// Binder DOM-elementer med `data-action="..."` til de relevante services.
/// Razor-views har INGEN inline `onclick`-handlers — de erklærer intention
/// via data-attributter, og denne binder hooker dem op ét sted.
///
/// Tilføjelse af nye actions: udvid `DataAction`-typen i `types.ts` og
/// switch-casen herinde — TypeScript fanger missing handlers.
///
/// Markup-konvention pr. action:
///   open-modal       data-target="<modal-id>"
///   close-modal      data-target="<modal-id>"
///   copy-text        data-text="<value>"
///   copy-all         (auto-finder [data-copy-value]-elementer i .panel)
///   toggle-password  (auto-finder nærmeste input)
///   toggle-switch    data-label="<label-til-toast>"
///   placeholder      data-label="<besked-til-toast>"

export interface BinderServices {
  modal: IModalService;
  toast: IToastService;
  clipboard: IClipboardService;
  toggles: IUiToggleService;
  theme: IThemeService;
  userActions: IUserActionsService;
  domainActions: IDomainActionsService;
  confirmer: IModalConfirmHelper;
  tokenActions: ITokenActionsService;
  settingsActions: ISettingsActionsService;
  auth: IAuthService;
}

export class EventBinder {
  constructor(private readonly services: BinderServices) { }

  /// Delegerer alle clicks på document-niveau. Det betyder at dynamisk
  /// indsatte elementer også fanges uden re-binding.
  bind(root: Document = document): void {
    root.addEventListener('click', (e: MouseEvent) => this.handleClick(e));
  }

  private handleClick(e: MouseEvent): void {
    const trigger = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-action]');
    if (!trigger) return;
    const action = trigger.getAttribute('data-action') as DataAction | null;
    if (!action) return;

    switch (action) {
      case 'open-modal':
        this.requireTarget(trigger, t => this.services.modal.open(t));
        break;
      case 'close-modal':
        this.requireTarget(trigger, t => this.services.modal.close(t));
        break;
      case 'copy-text': {
        const text = trigger.getAttribute('data-text') ?? '';
        void this.services.clipboard.copyText(trigger, text);
        break;
      }
      case 'copy-all':
        void this.services.clipboard.copyAll(trigger);
        break;
      case 'toggle-password':
        this.services.toggles.togglePasswordVisibility(trigger);
        break;
      case 'toggle-switch': {
        const label = trigger.getAttribute('data-label') ?? '';
        this.services.toggles.toggleSwitch(trigger, label);
        break;
      }
      case 'toggle-theme':
        this.services.theme.toggle();
        break;
      case 'placeholder': {
        const label = trigger.getAttribute('data-label') ?? 'Handling ikke implementeret endnu';
        this.services.toast.show(label, 'info');
        break;
      }
      case 'create-user':
        // Modal-id er hårdkodet matching `_CreateUser.cshtml` — kun én
        // create-user-modal pr. side. Hvis vi senere får flere modaler
        // af samme type, kan vi læse data-target på knappen.
        void this.services.userActions.createFromModal('modal-create-user');
        break;
      case 'disable-user': {
        const email = trigger.getAttribute('data-email') ?? '';
        if (!email) { console.warn('disable-user requires data-email', trigger); break; }
        void this.services.userActions.disable(email);
        break;
      }
      case 'restore-user': {
        const email = trigger.getAttribute('data-email') ?? '';
        if (!email) { console.warn('restore-user requires data-email', trigger); break; }
        void this.services.userActions.restore(email);
        break;
      }
      case 'delete-user': {
        const email = trigger.getAttribute('data-email') ?? '';
        if (!email) { console.warn('delete-user requires data-email', trigger); break; }
        this.services.userActions.deleteWithConfirm(email);
        break;
      }
      case 'delete-domain': {
        const name = trigger.getAttribute('data-name') ?? '';
        if (!name) { console.warn('delete-domain requires data-name', trigger); break; }
        this.services.domainActions.deleteWithConfirm(name);
        break;
      }
      case 'issue-dkim': {
        const name = trigger.getAttribute('data-name') ?? '';
        if (!name) { console.warn('issue-dkim requires data-name', trigger); break; }
        void this.services.domainActions.issueDkim(name);
        break;
      }
      case 'rotate-dkim': {
        const name = trigger.getAttribute('data-name') ?? '';
        const selector = trigger.getAttribute('data-selector') ?? '';
        if (!name || !selector) { console.warn('rotate-dkim requires data-name + data-selector', trigger); break; }
        void this.services.domainActions.rotateDkim(name, selector);
        break;
      }
      case 'delete-dkim': {
        const name = trigger.getAttribute('data-name') ?? '';
        const selector = trigger.getAttribute('data-selector') ?? '';
        if (!name || !selector) { console.warn('delete-dkim requires data-name + data-selector', trigger); break; }
        void this.services.domainActions.deleteDkim(name, selector);
        break;
      }
      case 'toggle-dkim-key': {
        const selector = trigger.getAttribute('data-selector') ?? '';
        if (!selector) { console.warn('toggle-dkim-key requires data-selector', trigger); break; }
        this.services.domainActions.toggleDkimKey(selector);
        break;
      }
      case 'confirm-delete':
        void this.services.confirmer.confirm();
        break;
      case 'create-domain':
        void this.services.domainActions.createFromModal('modal-create-domain');
        break;
      case 'verify-dns': {
        const domain = trigger.getAttribute('data-domain') ?? '';
        if (!domain) {
          console.warn('verify-dns requires data-domain', trigger);
          break;
        }
        void this.services.domainActions.verifyDns(domain);
        break;
      }
      case 'create-token':
        void this.services.tokenActions.createFromModal('modal-create-token');
        break;
      case 'revoke-token': {
        const id = trigger.getAttribute('data-token-id') ?? '';
        const name = trigger.getAttribute('data-token-name') ?? '';
        if (!id) {
          console.warn('revoke-token requires data-token-id', trigger);
          break;
        }
        void this.services.tokenActions.revoke(id, name);
        break;
      }
      case 'close-token-issued':
        this.services.tokenActions.closeIssuedModalAndReload();
        break;
      case 'save-mx-host':
        void this.services.settingsActions.saveMxHost();
        break;
      case 'login':
        void this.services.auth.login();
        break;
      case 'logout':
        void this.services.auth.logout();
        break;
      default: {
        // Compile-time exhaustiveness check: hvis vi tilføjer en ny
        // DataAction og glemmer at handle den, vil TS stoppe build her.
        const _exhaustive: never = action;
        void _exhaustive;
      }
    }
  }

  private requireTarget(trigger: HTMLElement, action: (id: string) => void): void {
    const target = trigger.getAttribute('data-target');
    if (!target) {
      console.warn('data-action requires data-target', trigger);
      return;
    }
    action(target);
  }
}
